import React from "react";
import get from "lodash/get";
import set from "lodash/set";
import isEqual from "lodash/isEqual";
import juration from "juration";
import Codemirror from "react-codemirror";
import "codemirror/mode/javascript/javascript";
import momentTimezone from "moment-timezone";
import Select from "react-select";
import {
  CircularProgress,
  RaisedButton,
  FlatButton,
  TextField,
  Checkbox,
  RadioButtonGroup,
  RadioButton,
  Slider,
  SelectField,
  MenuItem
} from "material-ui";
import { yellow500, red500, green500 } from "material-ui/styles/colors";
import { colors } from "material-ui/styles";
import { Link } from "react-router";

import { capitalize } from "../util";

import WorkingIndicator from "./partials/working-indicator.jsx";
import BaseOverlay from "./partials/overlays/base.jsx";
import BackButton from "./partials/back-button.jsx";
import Label from "./partials/label.jsx";
import InfoNotice from "./partials/info-notice.jsx";
import EventiveSelectField from "./partials/select-field.jsx";
import CurrencyField from "./partials/currency-field.jsx";
import PercentField from "./partials/percent-field.jsx";
import InlineNoticeLabel from "./partials/inline-notice-label.jsx";
import InjectedSelect from "./partials/injected-select.jsx";
import LinkLike from "./partials/link-like.jsx";
import DurationField from "./partials/duration-field.jsx";
import VirtualContentEditor from "./partials/virtual-content-editor.jsx";

import connectToStores from "alt-utils/lib/connectToStores";
import EventBucketActions from "../actions/EventBucketActions";
import EventBucketStore from "../stores/EventBucketStore";
import UserStore from "../stores/UserStore";

import ImageDropzone from "./partials/image-dropzone.jsx";
import DateTimeField from "./partials/datetime-field.jsx";

class EventBucketSettings extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      bucket: {},
      dirty: false,
      values: {
        name: "",
        timezone: "",
        frontend_url: "",
        custom_domain: "",
        frontend_config: {},
        frontend_config_text: ""
      },
      showCustomDomainField: false,
      showSubdomainStatus: false,
      showConfigEditor: false,
      showRushLinePriceEditor: false,
      showTaxEditor: false,
      showGstVatEditor: false,
      showFeeCapEditor: false,
      isSaving: false,
      lastUpdatedAt: null,
      currentMoment: momentTimezone(),

      showAdditionalSettingsModal: false,
      showFeeAndTaxSettings: false,
      showVirtualSettings: false
    };

    this.onChange = this.onChange.bind(this);
    this.onCustomDomainCheck = this.onCustomDomainCheck.bind(this);
    this.onSubdomainChange = this.onSubdomainChange.bind(this);
    this.save = this.save.bind(this);
    this.duplicate = this.duplicate.bind(this);
    this.delete = this.delete.bind(this);
    this.updateTime = this.updateTime.bind(this);
    this.archive = this.archive.bind(this);
  }

  static getStores() {
    return [EventBucketStore];
  }

  static getPropsFromStores() {
    return {
      getEventBucketById: EventBucketStore.getEventBucketById,
      getSubdomainTestResultsByEventBucketId:
        EventBucketStore.getSubdomainTestResultsByEventBucketId
    };
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object,
      muiTheme: React.PropTypes.object
    };
  }

  getStyles() {
    return {
      actions: {
        marginTop: 10
      },
      progress: {
        marginBottom: 50
      },
      subdomainField: {
        width: 165
      },
      subdomainSuffix: {
        fontSize: 16,
        fontWeight: "bold"
      },
      subdomainStatus: {
        color: "rgba(33, 33, 33, 0.498039)"
      },
      subdomainStatusIndicator: {
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: 5000,
        marginRight: 5
      },
      label: {
        color: "rgba(33, 33, 33, 0.498039)",
        fontSize: 12,
        marginBottom: 5
      },
      timezoneContainer: {
        marginTop: 10,
        width: 256
      }
    };
  }

  onChange(id, e, checked) {
    this.onChangeValue(
      id,
      typeof checked !== "undefined"
        ? !get(this.state.values, id)
        : e.target.value
    );
  }

  onChangeValue(id, value) {
    this.state.dirty = true;
    set(this.state.values, id, value);
    this.setState(this.state);
  }

  onCustomDomainCheck(e) {
    this.setState(
      {
        showCustomDomainField: !this.state.showCustomDomainField
      },
      () => {
        this.onChangeValue("custom_domain", "");
      }
    );
  }

  onSubdomainChange(e) {
    let { value } = e.target;

    // Block new subdomains from using "_" character. This character is not supported
    // for HTTPS subdomains on iOS Safari.
    if (value !== this.state.bucket.frontend_url) {
      value = value.replace(/_/g, "");
    }

    this.onChangeValue("frontend_url", value);
    this.setState({ showSubdomainStatus: true });
    if (value) {
      EventBucketActions.testSubdomain(this.props.params.eventBucketId, value);
    }
  }

  save() {
    let values = Object.assign({}, this.state.values);

    try {
      let parsedConfig = JSON.parse(values.frontend_config_text);
      delete values.frontend_config_text;
      if (!isEqual(parsedConfig, this.state.bucket.frontend_config)) {
        // If text was manually edited, use that
        values.frontend_config = JSON.stringify(parsedConfig);
      } else if (
        !isEqual(values.frontend_config, this.state.bucket.frontend_config)
      ) {
        // If frontend config object has been edited, use that
        values.frontend_config = JSON.stringify(values.frontend_config);
      } else {
        // Fall through - no frontend config update
        delete values.frontend_config;
      }
    } catch (e) {
      // Alert malformed JSON
      alert(`Error in advanced configuration: ${e.message}`);
      return;
    }

    this.setState({
      isSaving: true,
      lastUpdatedAt: this.state.bucket.updated_at
    });
    EventBucketActions.edit.defer(this.props.params.eventBucketId, values);
    window.scrollTo(0, 0);
  }

  duplicate() {
    this.context.router.push(
      `/event_buckets/${this.props.params.eventBucketId}/settings/duplicate`
    );
  }

  delete() {
    if (
      window.confirm(
        "ARE YOU SURE? This will destroy everything related to the bucket, including events, passes, and tickets.\n\nIf it is possible, Event Bucket Recovery after deletion will incur a $250 fee."
      )
    ) {
      EventBucketActions.delete(this.props.params.eventBucketId);
      this.context.router.push(`/welcome`);
    }
  }

  archive() {
    if (window.confirm("Are you sure?")) {
      this.onChangeValue("mode", "archived");
      this.save();
    }
  }

  updateTime() {
    this.setState({
      currentMoment: momentTimezone()
    });
  }

  componentDidMount() {
    EventBucketActions.fetchOne(this.props.params.eventBucketId);
    this.componentWillReceiveProps(this.props);

    this.timezoneInterval = setInterval(this.updateTime, 1000);
    this.updateTime();

    this.context.router.setRouteLeaveHook(this.props.route, () => {
      if (this.state.dirty) {
        return "Are you sure you wish to leave the event bucket editor? Your changes will not be saved.";
      }
    });
  }

  componentWillUnmount() {
    clearInterval(this.timezoneInterval);
  }

  componentWillReceiveProps(newProps) {
    var bucket = newProps.getEventBucketById(newProps.params.eventBucketId);

    if (
      Object.keys(this.state.values).length === 0 ||
      this.state.bucket.id !== newProps.params.eventBucketId ||
      (bucket && bucket.updated_at !== this.state.lastUpdatedAt)
    ) {
      this.setState({
        bucket,
        dirty: false,
        isSaving: false,
        showSubdomainStatus: false,
        lastUpdatedAt: bucket && bucket.updated_at,
        showCustomDomainField: bucket && !!bucket.custom_domain,
        showCutoffEditor: bucket && !!bucket.ticket_sales_cutoff,
        values: bucket
          ? {
            name: bucket.name,
            timezone: bucket.timezone,
            class: bucket.class,
            mode: bucket.mode,
            schedule_enabled: bucket.schedule_enabled,
            schedule_include_virtual: bucket.schedule_include_virtual,
            film_guide_enabled: bucket.film_guide_enabled,
            donations_enabled: bucket.donations_enabled,
            standalone_ticket_sales_enabled:
              bucket.standalone_ticket_sales_enabled,
            standalone_ticket_sales_locked:
              bucket.standalone_ticket_sales_locked,
            standalone_ticket_sales_locked_password:
              bucket.standalone_ticket_sales_locked_password,
            passholder_ticket_required_default:
              bucket.passholder_ticket_required_default,
            passholder_one_click: bucket.passholder_one_click,
            passholder_order_email_confirmation:
              bucket.passholder_order_email_confirmation,
            passholder_web_orders_allowed:
              bucket.passholder_web_orders_allowed,
            passholder_instant_ticket_oversell_allowed:
              bucket.passholder_instant_ticket_oversell_allowed,
            passholder_transfer_allowed: bucket.passholder_transfer_allowed,
            absorb_fee_percent: bucket.absorb_fee_percent,
            absorb_donation_fees: bucket.absorb_donation_fees,
            absorb_ticket_fees: bucket.absorb_ticket_fees,
            absorb_pass_fees: bucket.absorb_pass_fees,
            ticket_sales_cutoff: bucket.ticket_sales_cutoff,
            ticket_transfer_for_all_users_disabled:
              bucket.ticket_transfer_for_all_users_disabled,
            fee_cap: bucket.fee_cap,
            virtual_fee_amount: bucket.virtual_fee_amount,
            fee_at_box_office: bucket.fee_at_box_office,
            tax_percent: bucket.tax_percent,
            tax_for: bucket.tax_for,
            tax_at_box_office: bucket.tax_at_box_office,
            gst_vat_percent: bucket.gst_vat_percent,
            rush_line_enabled: bucket.rush_line_enabled,
            rush_line_default_price: bucket.rush_line_default_price,
            box_office_auto_open: bucket.box_office_auto_open,
            box_office_auto_populate: bucket.box_office_auto_populate,
            box_office_ticket_printer: bucket.box_office_ticket_printer,
            box_office_shift_tracking: bucket.box_office_shift_tracking,
            box_office_show_mark_scanned: bucket.box_office_show_mark_scanned,
            box_office_use_boca: bucket.box_office_use_boca,
            box_office_service_fee: bucket.box_office_service_fee,
            credits_enabled: bucket.credits_enabled,
            credits_face_price: bucket.credits_face_price,
            credits_levels: bucket.credits_levels
              ? bucket.credits_levels.length
                ? bucket.credits_levels
                : [{}]
              : [],
            credits_discount: bucket.credits_discount,
            credits_allowed_pass_buckets: bucket.credits_allowed_pass_buckets,
            virtual_festival_config: bucket.virtual_festival_config || {},
            virtual_content_preroll: bucket.virtual_content_preroll,
            virtual_content_postroll: bucket.virtual_content_postroll,
            enable_order_all_starred: bucket.enable_order_all_starred,
            enable_guest_checkout: bucket.enable_guest_checkout,
            enable_cart: bucket.enable_cart,
            enable_balloting: bucket.enable_balloting,
            enable_custom_event_ids: bucket.enable_custom_event_ids,
            enable_eventival_login: bucket.enable_eventival_login,
            enable_nfc: bucket.enable_nfc,
            enable_virtual_festival: bucket.enable_virtual_festival,
            defer_all_unlock_fees: bucket.defer_all_unlock_fees,
            enable_title_delivery: bucket.enable_title_delivery,
            title_delivery_config: bucket.title_delivery_config || {},
            frontend_url: get(bucket, "frontend_url", ""),
            custom_domain: get(bucket, "custom_domain", ""),
            frontend_config: JSON.parse(
              JSON.stringify(get(bucket, "frontend_config", {}))
            ),
            frontend_config_text: JSON.stringify(
              get(bucket, "frontend_config", {}),
              null,
              2
            )
          }
          : {}
      });
    }
  }

  render() {
    var styles = this.getStyles();

    const isVirtualExpired =
      (UserStore.getUser() || {}).virtual_festival_plan_status === "expired" &&
      !UserStore.isGod() &&
      this.state.values.enable_virtual_festival;

    const showBoxOfficeFeeSettings =
      (this.state.bucket || {}).pricing_plan === "sundance" ||
      (this.state.bucket || {}).pricing_plan === "cphdox";

    let saveButton = (
      <RaisedButton
        label={
          isVirtualExpired
            ? "Cannot save since Eventive Virtual expired"
            : this.state.dirty
              ? "Save changes"
              : "No changes to save"
        }
        primary={true}
        disabled={!this.state.dirty || isVirtualExpired}
        onTouchTap={this.save}
      />
    );

    if (this.state.isSaving || !this.state.bucket) {
      return <WorkingIndicator fullPage={true} />;
    }

    let subdomainResults = this.props.getSubdomainTestResultsByEventBucketId(
      this.props.params.eventBucketId
    ),
      subdomainStatus = get(
        subdomainResults,
        this.state.values.frontend_url,
        false
      );

    return (
      <div>
        {this.props.children /* for configurator modal */}
        <div>
          <BackButton
            label="Event bucket"
            location={`/event_buckets/${this.props.params.eventBucketId}`}
          />
          <span style={{ marginLeft: 7 }}>{saveButton}</span>
        </div>
        <h1 className="page-title">{get(this.state, "values.name", "")} Settings</h1>
        {UserStore.hasPermission("edit_event_bucket") ? (
          <div>
            <InfoNotice style={{ marginTop: 20 }}>
              Check out our{" "}
              <a
                href="http://help.eventive.org/events-and-tickets/create-event-buckets"
                target="_blank"
              >
                guide to creating event buckets
              </a>
              .
            </InfoNotice>
            <div>
              <TextField
                value={get(this.state, "values.name", "")}
                floatingLabelText="Name"
                floatingLabelFixed={true}
                onChange={e => this.onChangeValue("name", e.target.value)}
              />
            </div>
            <div>
              <TextField
                style={styles.subdomainField}
                value={get(this.state, "values.frontend_url", "")}
                floatingLabelText="Subdomain"
                floatingLabelFixed={true}
                onChange={this.onSubdomainChange}
              />
              <span style={styles.subdomainSuffix}>.eventive.org</span>
              {this.state.showSubdomainStatus ? (
                <div style={styles.subdomainStatus}>
                  <span
                    style={Object.assign({}, styles.subdomainStatusIndicator, {
                      backgroundColor:
                        subdomainStatus === true
                          ? green500
                          : subdomainStatus === false
                            ? yellow500
                            : red500
                    })}
                  />
                  {subdomainStatus === true
                    ? "Looks good!"
                    : subdomainStatus === false
                      ? "Checking..."
                      : subdomainStatus}
                </div>
              ) : null}
            </div>
            <div style={styles.timezoneContainer}>
              <div style={styles.label}>Timezone</div>
              <Select
                placeholder="None selected"
                value={get(this.state, "values.timezone", "")}
                autosize={true}
                options={momentTimezone.tz.names().map(n => {
                  return {
                    label: `${n} (${this.state.currentMoment
                      .tz(n)
                      .format("LT")})`,
                    value: n
                  };
                })}
                onChange={option => {
                  this.onChangeValue("timezone", option ? option.value : "");
                }}
              />
            </div>
            <div style={{ marginTop: 20 }}>
              <h3>Site features</h3>
              <div style={{ marginBottom: 10 }}>
                {this.state.isEditingMode ? (
                  <div style={{}}>
                    <div style={{ marginBottom: 5 }}>
                      Event bucket mode:
                      <LinkLike
                        style={{ marginLeft: 10 }}
                        onTouchTap={() =>
                          this.setState({ isEditingMode: false })
                        }
                      >
                        Never mind
                      </LinkLike>
                    </div>
                    <RadioButtonGroup
                      name="mode"
                      style={{ marginLeft: "0em" }}
                      valueSelected={this.state.values.mode}
                      onChange={(e, v) => {
                        this.setState({ isEditingMode: false }, () => {
                          this.onChangeValue("mode", v);
                        });
                      }}
                    >
                      <RadioButton
                        value="default"
                        label={
                          <span>
                            <strong>Default</strong>: All features enabled. Sell
                            both passes & standalone tickets.
                          </span>
                        }
                      />
                      <RadioButton
                        value="pass_only"
                        label={
                          <span>
                            <strong>Pass only</strong>: Sell passes but no
                            standalone tickets. Ticket reservations for
                            passholders.
                          </span>
                        }
                      />
                      <RadioButton
                        value="standalone_ticket_only"
                        label={
                          <span>
                            <strong>Standalone tickets only</strong>: Sell only
                            standalone tickets. No passes.
                          </span>
                        }
                      />
                      <RadioButton
                        value="box_office_only"
                        label={
                          <span>
                            <strong>Box office only</strong>: No online sales.
                            Only box office / rush line.
                          </span>
                        }
                      />
                      <RadioButton
                        value="sales_disabled"
                        label={
                          <span>
                            <strong>All sales disabled</strong>: No online or
                            box office sales.
                          </span>
                        }
                      />
                      <RadioButton
                        value="archived"
                        label={
                          <span>
                            <strong>Archived</strong>: Sales disabled. Moved to
                            the "Archive" section.
                          </span>
                        }
                      />
                    </RadioButtonGroup>
                  </div>
                ) : (
                  <div>
                    Event bucket mode:{" "}
                    <strong>
                      {capitalize(
                        (this.state.values.mode || "")
                          .replace(/_/g, " ")
                          .replace("ticket", "tickets") // "Standalone ticket only" => "Standalone tickets only"
                      )}
                    </strong>{" "}
                    &middot;{" "}
                    <LinkLike
                      onTouchTap={() => this.setState({ isEditingMode: true })}
                    >
                      Change mode
                    </LinkLike>
                  </div>
                )}
              </div>
              <div>
                <Checkbox
                  label={
                    isVirtualExpired
                      ? "Eventive Virtual expired –⁠ contact us"
                      : "Eventive Virtual activated"
                  }
                  checked={this.state.values.enable_virtual_festival || false}
                  disabled
                />
              </div>
              <div>
                <Checkbox
                  label="Native schedule publicly visible"
                  checked={this.state.values.schedule_enabled || false}
                  onCheck={this.onChange.bind(this, "schedule_enabled")}
                />
              </div>
              <div>
                <Checkbox
                  label="Native film guide publicly visible"
                  checked={this.state.values.film_guide_enabled || false}
                  onCheck={this.onChange.bind(this, "film_guide_enabled")}
                />
              </div>
              {this.state.values.mode !== "pass_only" ? (
                <div>
                  <Checkbox
                    label="Standalone tickets on sale to public"
                    checked={
                      this.state.values.standalone_ticket_sales_enabled || false
                    }
                    onCheck={this.onChange.bind(
                      this,
                      "standalone_ticket_sales_enabled"
                    )}
                  />
                </div>
              ) : null}
              <div>
                <Checkbox
                  label="Accept donations on site"
                  checked={this.state.values.donations_enabled || false}
                  onCheck={this.onChange.bind(this, "donations_enabled")}
                />
                {this.state.values.donations_enabled ? (
                  <div style={{ marginLeft: 40 }}>
                    {this.state.values.donations_enabled !==
                      this.state.bucket.donations_enabled ? (
                      <i>
                        Save your changes to view a link to the donations page.
                      </i>
                    ) : (
                      <div>
                        <a
                          target="_blank"
                          href={
                            this.state.bucket && this.state.bucket.urls.donate
                          }
                        >
                          Direct donation link
                        </a>
                        &nbsp;&middot;&nbsp;
                        <Link
                          to={`/event_buckets/${this.state.bucket.id}/settings/configurator/donations`}
                        >
                          Configure in Configurator
                        </Link>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
              <div>
                <Checkbox
                  label="Accept gift card purchases on site"
                  checked={get(this.state.values, "frontend_config.gift_cards_frontend_enabled", false)}
                  onCheck={this.onChange.bind(this, "frontend_config.gift_cards_frontend_enabled")}
                />
                {this.state.values.frontend_config.gift_cards_enabled && this.state.values.frontend_config.gift_cards_frontend_enabled
                  ? (
                    <div style={{ marginLeft: 40 }}>
                      {this.state.values.frontend_config.gift_cards_frontend_enabled !== this.state.bucket.frontend_config.gift_cards_frontend_enabled ? (
                        <i>Save changes to edit gift card information in configurator</i>
                      ) : (
                        <Link
                          to={`/event_buckets/${this.state.bucket.id}/settings/configurator/gift_cards`}
                        >
                          Configure in Configurator
                        </Link>
                      )}
                    </div>
                  )
                  : null}
              </div>
              {UserStore.isGod() ? (<div>
                <Checkbox
                  label="Add a membership section on site"
                  checked={get(this.state.values, "frontend_config.memberships_enabled", false)}
                  onCheck={this.onChange.bind(this, "frontend_config.memberships_enabled")}
                />
                {this.state.values.frontend_config.memberships_enabled
                  ? (
                    <div style={{ marginLeft: 40 }}>
                      {this.state.values.frontend_config.memberships_enabled !== this.state.bucket.frontend_config.memberships_enabled ? (
                        <i>Save changes to edit membership information in configurator</i>
                      ) : (
                        <Link
                          to={`/event_buckets/${this.state.bucket.id}/settings/configurator/memberships`}
                        >
                          Configure in Configurator
                        </Link>
                      )}
                    </div>
                  )
                  : null}
              </div>) : null}
            </div>
          </div>
        ) : null}
        {this.state.values.enable_virtual_festival ? (
          <div style={{ marginTop: 20 }}>
            <h3>Eventive Virtual settings</h3>
            <div>
              Customize your Eventive Virtual channel and its discoverability on
              the Eventive platform.
            </div>
            <div style={{ marginTop: 10 }}>
              <RaisedButton
                label="Open Eventive Virtual settings"
                onTouchTap={() => this.setState({ showVirtualSettings: true })}
              />
            </div>
          </div>
        ) : null}
        {UserStore.hasPermission([
          "edit_event_bucket",
          "manage_configurator"
        ]) ? (
          <div style={{ marginTop: 20 }}>
            <h3>Configurator</h3>
            <div>Customize every aspect of your customers' experiences.</div>
            <div style={{ marginTop: 10 }}>
              <RaisedButton
                style={{ marginRight: 7 }}
                label="Launch configurator"
                primary={true}
                onTouchTap={() =>
                  this.context.router.push(
                    `/event_buckets/${this.state.bucket.id}/settings/configurator`
                  )
                }
              />
              {this.state.showConfigEditor ? (
                <RaisedButton
                  style={{ marginBottom: 10 }}
                  label="Hide advanced editor"
                  onTouchTap={() => this.setState({ showConfigEditor: false })}
                />
              ) : (
                <RaisedButton
                  label="Show advanced editor"
                  onTouchTap={() => this.setState({ showConfigEditor: true })}
                />
              )}
            </div>
            {this.state.showConfigEditor ? (
              <div>
                <Codemirror
                  style={{ border: "1px solid #ccc;" }}
                  value={get(this.state, "values.frontend_config_text", "")}
                  onChange={this.onChangeValue.bind(
                    this,
                    "frontend_config_text"
                  )}
                  options={{
                    lineWrapping: true,
                    lineNumbers: true,
                    tabSize: 2,
                    indentUnit: 2,
                    mode: { name: "javascript", json: true }
                  }}
                />
              </div>
            ) : null}
          </div>
        ) : null}
        <div style={{ marginTop: 20 }}>
          <h3>Fee & tax settings</h3>
          <div>
            Configure how fees and taxes are charged or passed on to your
            customers.
          </div>
          <div style={{ marginTop: 10 }}>
            <RaisedButton
              label="Open fee & tax settings"
              onTouchTap={() => this.setState({ showFeeAndTaxSettings: true })}
            />
          </div>
        </div>
        {UserStore.hasPermission([
          "edit_event_bucket",
          "manage_embed_wizard"
        ]) ? (
          <div style={{ marginTop: 20 }}>
            <h3>Embed in your website</h3>
            <div>
              Embed fully functional components of your Eventive native site
              within your own website.
            </div>
            <div style={{ marginTop: 10 }}>
              <RaisedButton
                label="Open embed wizard"
                onTouchTap={() =>
                  this.context.router.push(
                    `/event_buckets/${this.state.bucket.id}/settings/embed`
                  )
                }
              />
              <RaisedButton
                style={{ marginLeft: 7 }}
                label={
                  <span>
                    Open Eventive Everywhere{" "}
                    <InlineNoticeLabel
                      style={{ marginLeft: 5 }}
                      label="Advanced"
                    />
                  </span>
                }
                onTouchTap={() =>
                  this.context.router.push(
                    `/event_buckets/${this.state.bucket.id}/settings/everywhere`
                  )
                }
              />
            </div>
          </div>
        ) : null}
        {UserStore.hasPermission("edit_event_bucket") ? (
          <div>
            <div style={{ marginTop: 20, marginBottom: 30 }}>
              <h3>Additional settings</h3>
              <div>More options to make your event bucket yours.</div>
              <div style={{ marginTop: 10 }}>
                <RaisedButton
                  label="Open additional settings"
                  onTouchTap={() =>
                    this.setState({ showAdditionalSettingsModal: true })
                  }
                />
              </div>
            </div>
            <div style={styles.actions}>
              {saveButton}
              <RaisedButton
                label="Duplicate"
                onTouchTap={this.duplicate}
                disabled={this.state.dirty}
                style={{ marginLeft: 7 }}
              />
              {this.state.values.mode !== "archived" && (
                <RaisedButton
                  label={"Archive Event Bucket"}
                  style={{ marginLeft: 7 }}
                  onTouchTap={this.archive}
                />
              )}
              {UserStore.isGod() && (
                <RaisedButton
                  label="Delete event bucket"
                  style={{ marginLeft: 7 }}
                  onTouchTap={this.delete}
                />
              )}
            </div>
          </div>
        ) : null}
        <BaseOverlay
          open={this.state.showFeeAndTaxSettings}
          onRequestClose={() => this.setState({ showFeeAndTaxSettings: false })}
          hideTitleAndFooterBorders={true}
          showCloseButton={true}
        >
          <div>
            <h2>Fee and tax settings</h2>
            {UserStore.hasPermission("manage_financial") ? (
              <div style={{ marginTop: 20 }}>
                <h3>Physical event & pass sales customer-facing fees</h3>
                <div>
                  <RadioButtonGroup
                    name="fee_absorption"
                    valueSelected={
                      this.state.values.absorb_fee_percent === 100
                        ? "absorb"
                        : this.state.values.absorb_fee_percent === 0
                          ? "customer"
                          : "split"
                    }
                    onChange={(e, v) =>
                      this.onChangeValue(
                        "absorb_fee_percent",
                        v === "absorb" ? 100 : v === "split" ? 50 : 0
                      )
                    }
                  >
                    <RadioButton
                      value="customer"
                      label="Pass fees on to customer"
                    />
                    <RadioButton value="absorb" label="Absorb fees" />
                    <RadioButton value="split" label="Split fees" />
                  </RadioButtonGroup>
                  {[100, 0].indexOf(this.state.values.absorb_fee_percent) ===
                    -1 ? (
                    <div>
                      <div>
                        <Label>Percent of fee to absorb</Label>
                        <div>
                          0%{" "}
                          <Slider
                            min={0}
                            max={100}
                            step={1}
                            style={{
                              margin: "-24px 10px -52px",
                              width: 256,
                              display: "inline-block"
                            }}
                            value={get(
                              this.state,
                              "values.absorb_fee_percent",
                              0
                            )}
                            onChange={(e, value) =>
                              this.onChangeValue("absorb_fee_percent", value)
                            }
                          />{" "}
                          100%
                        </div>
                      </div>
                      <div style={{ marginTop: 10 }}>
                        With your current settings, customers will pay{" "}
                        <b>
                          {100 -
                            get(this.state, "values.absorb_fee_percent", 0)}
                          %
                        </b>{" "}
                        of the fee.
                      </div>
                    </div>
                  ) : null}
                </div>
                {this.state.values.absorb_fee_percent !== 100 ? (
                  <div>
                    <Checkbox
                      style={{ marginTop: 15 }}
                      label="Cap max fee per transaction"
                      checked={
                        this.state.values.fee_cap || this.state.showFeeCapEditor
                      }
                      onCheck={(e, checked) => {
                        this.setState({ showFeeCapEditor: checked }, () => {
                          !checked && this.onChangeValue("fee_cap", 0);
                        });
                      }}
                    />
                    {this.state.showFeeCapEditor ||
                      this.state.values.fee_cap ? (
                      <CurrencyField
                        floatingLabelText="Max fee per transaction"
                        floatingLabelFixed={true}
                        placeholder="$0.00"
                        value={this.state.values.fee_cap}
                        onChange={e =>
                          this.setState({ showFeeCapEditor: true }, () =>
                            this.onChangeValue("fee_cap", e.target.value)
                          )
                        }
                        style={{ marginBottom: 15 }}
                      />
                    ) : null}
                  </div>
                ) : null}
                {this.state.values.donations_enabled &&
                  this.state.values.absorb_fee_percent !== 100 ? (
                  <Checkbox
                    label="Absorb donation fees"
                    checked={this.state.values.absorb_donation_fees}
                    onCheck={this.onChange.bind(this, "absorb_donation_fees")}
                  />
                ) : null}
                {this.state.values.absorb_fee_percent !== 100 ? (
                  <Checkbox
                    label="Absorb fees on ticket sales"
                    checked={this.state.values.absorb_ticket_fees}
                    onCheck={this.onChange.bind(this, "absorb_ticket_fees")}
                  />
                ) : null}
                {this.state.values.absorb_fee_percent !== 100 ? (
                  <Checkbox
                    label="Absorb fees on pass sales"
                    checked={this.state.values.absorb_pass_fees}
                    onCheck={this.onChange.bind(this, "absorb_pass_fees")}
                  />
                ) : null}
              </div>
            ) : null}
            {UserStore.hasPermission("manage_financial") &&
              this.state.values.enable_virtual_festival ? (
              <div style={{ marginTop: 20 }}>
                <h3>Virtual screening customer-facing fees</h3>
                <RadioButtonGroup
                  name="virtual_fees"
                  valueSelected={
                    this.state.values.virtual_fee_amount === -1
                      ? "passon"
                      : this.state.values.virtual_fee_amount ||
                        this.state.showVirtualFeeEditor
                        ? "fee"
                        : "nofee"
                  }
                  onChange={(e, v) =>
                    this.setState({ showVirtualFeeEditor: v === "fee" }, () => {
                      this.onChangeValue(
                        "virtual_fee_amount",
                        v === "nofee" ? 0 : v === "passon" ? -1 : 125
                      );
                    })
                  }
                >
                  {[
                    <RadioButton
                      value="nofee"
                      label="No additional fees (recommended)"
                    />,
                    <RadioButton
                      value="fee"
                      label="Add a fee for virtual unlocks"
                    />,
                    UserStore.isGod() && (
                      <RadioButton
                        value="passon"
                        label="[GOD ONLY/REQUIRES CUSTOM AGREEMENT] Pass on exact ticketing transaction fee"
                      />
                    )
                  ].filter(x => !!x)}
                </RadioButtonGroup>
                {this.state.values.virtual_fee_amount !== -1 &&
                  (this.state.showVirtualFeeEditor ||
                    this.state.values.virtual_fee_amount) ? (
                  <CurrencyField
                    floatingLabelText="Virtual ticket unlock customer fee"
                    floatingLabelFixed={true}
                    placeholder="$0.00"
                    value={this.state.values.virtual_fee_amount}
                    onChange={e =>
                      this.setState({ showVirtualFeeEditor: true }, () =>
                        this.onChangeValue("virtual_fee_amount", e.target.value)
                      )
                    }
                  />
                ) : null}
              </div>
            ) : null}
            {showBoxOfficeFeeSettings ? (
              <div style={{ marginTop: 20 }}>
                <h3>Fees at the box office</h3>
                <Checkbox
                  style={{}}
                  label="Pass on fees to customer at the box office"
                  checked={this.state.values.fee_at_box_office}
                  onCheck={() =>
                    this.onChangeValue(
                      "fee_at_box_office",
                      !this.state.values.fee_at_box_office
                    )
                  }
                />
              </div>
            ) : null}
            {UserStore.hasPermission("manage_financial") ? (
              <div style={{ marginTop: 20 }}>
                <h3>Sales tax</h3>
                <Checkbox
                  label="Add sales tax"
                  checked={
                    this.state.values.tax_percent || this.state.showTaxEditor
                  }
                  onCheck={(e, checked) => {
                    this.setState({ showTaxEditor: checked }, () => {
                      !checked && this.onChangeValue("tax_percent", 0);
                    });
                  }}
                />
                {this.state.showTaxEditor || this.state.values.tax_percent ? (
                  <div style={{ marginBottom: 15 }}>
                    <PercentField
                      floatingLabelText="Sales tax percent"
                      floatingLabelFixed={true}
                      placeholder="0.00%"
                      value={this.state.values.tax_percent}
                      onChange={e =>
                        this.setState({ showTaxEditor: true }, () =>
                          this.onChangeValue("tax_percent", e.target.value)
                        )
                      }
                    />
                    {(() => {
                      let types = ["pass", "ticket", "item"],
                        isAll = types.every(
                          t => this.state.values.tax_for.indexOf(t) !== -1
                        );
                      return (
                        <div>
                          <Label>Charge tax on</Label>
                          <RadioButtonGroup
                            name="tax_for"
                            valueSelected={isAll ? "all" : "some"}
                            onChange={(e, v) =>
                              this.onChangeValue(
                                "tax_for",
                                v === "some" ? [] : types
                              )
                            }
                          >
                            <RadioButton value="all" label="All order types" />
                            <RadioButton
                              value="some"
                              label="Some order types"
                            />
                          </RadioButtonGroup>
                          {!isAll ? (
                            <div style={{ marginTop: "0.5em" }}>
                              {types.map(t => {
                                return (
                                  <Checkbox
                                    style={{ marginLeft: "3em" }}
                                    label={`${capitalize(t)} orders`}
                                    checked={
                                      this.state.values.tax_for.indexOf(t) !==
                                      -1
                                    }
                                    onCheck={() => {
                                      let index =
                                        this.state.values.tax_for.indexOf(t);
                                      if (index === -1) {
                                        this.state.values.tax_for.push(t);
                                      } else {
                                        this.state.values.tax_for.splice(
                                          index,
                                          1
                                        );
                                      }
                                      this.onChangeValue(
                                        "tax_for",
                                        this.state.values.tax_for
                                      );
                                    }}
                                  />
                                );
                              })}
                            </div>
                          ) : null}
                          <Checkbox
                            style={{ marginTop: 15 }}
                            label="Charge sales tax at Box Office in addition to online"
                            checked={this.state.values.tax_at_box_office}
                            onCheck={() =>
                              this.onChangeValue(
                                "tax_at_box_office",
                                !this.state.values.tax_at_box_office
                              )
                            }
                          />
                        </div>
                      );
                    })()}
                  </div>
                ) : null}
                <Checkbox
                  style={{}}
                  label="Report GST/VAT (in generated reports)"
                  checked={
                    this.state.values.gst_vat_percent ||
                    this.state.showGstVatEditor
                  }
                  onCheck={(e, checked) => {
                    this.setState({ showGstVatEditor: checked }, () => {
                      !checked && this.onChangeValue("gst_vat_percent", 0);
                    });
                  }}
                />
                {this.state.showGstVatEditor ||
                  this.state.values.gst_vat_percent ? (
                  <PercentField
                    floatingLabelText="GST/VAT percent"
                    floatingLabelFixed={true}
                    placeholder="0.00%"
                    value={this.state.values.gst_vat_percent}
                    onChange={e =>
                      this.setState({ showGstVatEditor: true }, () =>
                        this.onChangeValue("gst_vat_percent", e.target.value)
                      )
                    }
                  />
                ) : null}
              </div>
            ) : null}
            <div style={{ marginTop: 20 }}>
              {saveButton}
              <RaisedButton
                label="Close"
                style={{ marginLeft: 7 }}
                onTouchTap={() =>
                  this.setState({ showFeeAndTaxSettings: false })
                }
              />
            </div>
          </div>
        </BaseOverlay>
        <BaseOverlay
          open={this.state.showVirtualSettings}
          onRequestClose={() => this.setState({ showVirtualSettings: false })}
          hideTitleAndFooterBorders={true}
          showCloseButton={true}
        >
          <div>
            <h2>Eventive Virtual settings</h2>
            <div>
              <div
                style={{
                  marginTop: 20,
                  border: `1px solid ${colors.blue100}`,
                  backgroundColor: colors.blue50,
                  padding: "1em 1.5em",
                  borderRadius: "1em"
                }}
              >
                <h3>Channel discovery</h3>
                <div>
                  Promote this event bucket channel in the Eventive Discovery
                  Portal and reach over two million film fans.
                </div>
                <Checkbox
                  style={{ marginTop: "0.5em" }}
                  label="Yes, promote in public Eventive Discovery Portal"
                  checked={get(
                    this.state,
                    "values.frontend_config.virtual_festival_show_in_directory",
                    false
                  )}
                  onCheck={(e, checked) => {
                    this.onChangeValue(
                      "frontend_config.virtual_festival_show_in_directory",
                      checked
                    );
                  }}
                />
                {get(
                  this.state,
                  "values.frontend_config.virtual_festival_show_in_directory",
                  false
                ) ? (
                  <div>
                    <div>
                      <TextField
                        floatingLabelText={`Channel title (${get(
                          this.state,
                          "values.frontend_config.virtual_festival_display_name",
                          ""
                        ).length
                          }/40)`}
                        floatingLabelFixed={true}
                        style={{ width: "100%" }}
                        placeholder="Shown in Discovery Portal. Up to 40 characters."
                        value={get(
                          this.state,
                          "values.frontend_config.virtual_festival_display_name",
                          ""
                        ).slice(0, 40)}
                        onChange={e => {
                          this.onChangeValue(
                            "frontend_config.virtual_festival_display_name",
                            e.target.value
                              .slice(0, 40)
                              .replace(/(\n|\s+)/g, " ")
                          );
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between"
                      }}
                    >
                      <div style={{ flex: 1, marginRight: "2em" }}>
                        <EventiveSelectField
                          label="Channel type"
                          placeholder="Choose channel type"
                          value={get(
                            this.state,
                            "values.frontend_config.virtual_festival_discovery_type",
                            ""
                          )}
                          options={[
                            {
                              label: "Film festival / screening series",
                              value: "film_festival"
                            },
                            {
                              label: "Single film release",
                              value: "single_film"
                            },
                            {
                              label: "Arthouse / virtual cinema",
                              value: "cinema"
                            },
                            {
                              label: "Special event",
                              value: "special_event"
                            },
                            {
                              label: "Repertory catalog",
                              value: "repertory_catalog"
                            }
                          ]}
                          onChange={option => {
                            this.onChangeValue(
                              "frontend_config.virtual_festival_discovery_type",
                              option ? option.value : ""
                            );
                          }}
                        />
                        <EventiveSelectField
                          label="Channel tags (up to 5)"
                          placeholder="Add tags to help viewers find your channel"
                          multi
                          value={get(
                            this.state,
                            "values.frontend_config.virtual_festival_discovery_tags",
                            ""
                          )}
                          options={
                            // prettier-ignore
                            ["Accessible", "Action", "Adventure", "American Indian", "Animation", "Anime", "Arab ", "Arthouse", "Black", "Canadian", "Children", "Christian", "Classic", "Comedy", "Community", "Documentary", "Drama", "Environmental", "Epic", "Experimental", "Faith and Spirituality", "Family", "Fantasy", "Film Market", "Fitness", "Food and Wine", "French", "Gender Issues", "General Interest", "Horror", "Indian", "International ", "Irish", "Italian", "Jewish", "Latino", "LGBTQ", "Local Filmmakers", "Mind and Body", "Mountain", "Music", "Musicals", "Mystery", "New Filmmakers", "News", "Outdoors", "Performing Arts", "Period Piece", "Podcast", "Reality TV", "Science", "Science Fiction", "Shorts", "Social Justice", "Sports", "Talk Shows", "Television", "Thrillers", "Video Games", "Web / New Media", "Westerns", "Women", "Young Filmmakers"]
                              .map(x => ({ label: x, value: x }))
                          }
                          onChange={v => {
                            this.onChangeValue(
                              "frontend_config.virtual_festival_discovery_tags",
                              v ? v.map(x => x.value).slice(0, 5) : []
                            );
                          }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <Label>Channel feature graphic</Label>
                        <div style={{}}>
                          {get(
                            this.state,
                            "values.frontend_config.virtual_festival_image",
                            ""
                          ) ? (
                            <div>
                              <img
                                src={get(
                                  this.state,
                                  "values.frontend_config.virtual_festival_image",
                                  ""
                                )}
                                style={{
                                  display: "block",
                                  height: 75,
                                  border: "1px solid #ccc",
                                  marginBottom: 2
                                }}
                              />
                              <LinkLike
                                onTouchTap={() =>
                                  this.onChangeValue(
                                    "frontend_config.virtual_festival_image",
                                    ""
                                  )
                                }
                              >
                                Replace with new graphic
                              </LinkLike>
                            </div>
                          ) : (
                            <ImageDropzone
                              crop={{ width: 1920, height: 1080 }}
                              style={{
                                fontSize: "0.75em",
                                lineHeight: "1.25",
                                padding: "30px 10px",
                                border: "1px dashed #ccc",
                                cursor: "pointer",
                                backgroundColor: "rgba(255,255,255,1)",
                                textAlign: "center"
                              }}
                              onDrop={files => {
                                EventBucketActions.uploadFile(
                                  this.props.params.eventBucketId,
                                  `virtual_festival_image`,
                                  files[0],
                                  ({ url }) => {
                                    this.onChangeValue(
                                      "frontend_config.virtual_festival_image",
                                      url
                                    );
                                  }
                                );
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 12,
                                  marginBottom: 5
                                }}
                              >
                                Drop an image here, or click to select.
                                <br />
                                <strong>
                                  Required in order to be displayed in Portal.
                                </strong>
                              </div>
                            </ImageDropzone>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <TextField
                        floatingLabelText={`Channel short description (${get(
                          this.state,
                          "values.frontend_config.virtual_festival_discovery_short_description",
                          ""
                        ).length
                          }/300)`}
                        floatingLabelFixed={true}
                        style={{ width: "100%" }}
                        multiLine={2}
                        placeholder="Up to 300 characters. Shown in Discovery Portal, TV apps, social media, and more."
                        value={get(
                          this.state,
                          "values.frontend_config.virtual_festival_discovery_short_description",
                          ""
                        )}
                        onChange={e => {
                          this.onChangeValue(
                            "frontend_config.virtual_festival_discovery_short_description",
                            e.target.value
                              .slice(0, 300)
                              .replace(/(\n|\s+)/g, " ")
                          );
                        }}
                      />
                    </div>
                    <div style={{ display: "flex" }}>
                      <div style={{ flex: "1 1 0%", marginRight: "2em" }}>
                        <Label>Channel promotion geography</Label>
                        <div style={{ marginBottom: "1em" }}>
                          If your channel should only be promoted to particular
                          geographic regions, indicate so here.
                        </div>
                        <Checkbox
                          style={{ marginTop: "1em" }}
                          label="Channel is geographically focused"
                          checked={get(
                            this.state,
                            "values.frontend_config.virtual_festival_discovery_geographic_regions",
                            false
                          )}
                          onCheck={(e, checked) => {
                            this.onChangeValue(
                              "frontend_config.virtual_festival_discovery_geographic_regions",
                              checked ? [] : false
                            );
                          }}
                        />
                        {get(
                          this.state,
                          "values.frontend_config.virtual_festival_discovery_geographic_regions",
                          false
                        ) ? (
                          <InjectedSelect
                            value={get(
                              this.state,
                              "values.frontend_config.virtual_festival_discovery_geographic_regions",
                              []
                            )}
                            style={{ marginTop: "1em" }}
                            types={["regions", "countries", "continents"]}
                            placeholderText="Select regions/states, countries, etc..."
                            multi={true}
                            noResultsText={
                              <span>
                                No regions found. Try entering a continent,
                                country, or province.
                              </span>
                            }
                            labelFormatter={v => v.name}
                            onChange={selected =>
                              this.onChangeValue(
                                "frontend_config.virtual_festival_discovery_geographic_regions",
                                selected
                              )
                            }
                            eventBucketId={this.props.params.eventBucketId}
                          />
                        ) : null}
                      </div>
                      <div style={{ flex: "1 1 0%" }}>
                        <Label>Channel availability dates</Label>
                        <div>
                          If your channel is only available during a particular
                          time window, indicate so here.
                        </div>
                        <Checkbox
                          style={{ marginTop: "1em" }}
                          label="Channel is date-restricted"
                          checked={get(
                            this.state,
                            "values.frontend_config.virtual_festival_discovery_dates",
                            false
                          )}
                          onCheck={(e, checked) => {
                            this.onChangeValue(
                              "frontend_config.virtual_festival_discovery_dates",
                              get(
                                this.state,
                                "values.frontend_config.virtual_festival_discovery_dates"
                              )
                                ? false
                                : {
                                  start: momentTimezone()
                                    .add(2, "weeks")
                                    .startOf("day")
                                    .format("MM-DD-YYYY"),
                                  end: momentTimezone()
                                    .add(3, "weeks")
                                    .startOf("day")
                                    .format("MM-DD-YYYY")
                                }
                            );
                          }}
                        />
                        {get(
                          this.state,
                          "values.frontend_config.virtual_festival_discovery_dates",
                          false
                        ) ? (
                          <div style={{}}>
                            <DateTimeField
                              value={get(
                                this.state,
                                "values.frontend_config.virtual_festival_discovery_dates.start",
                                ""
                              )}
                              label={"Start date"}
                              hideTime
                              useMoment={true}
                              unclearable={true}
                              onChange={(e, v) =>
                                this.onChangeValue(
                                  "frontend_config.virtual_festival_discovery_dates.start",
                                  v.format("MM-DD-YYYY")
                                )
                              }
                              style={{
                                display: "inline-block",
                                marginRight: 24
                              }}
                            />
                            <DateTimeField
                              value={get(
                                this.state,
                                "values.frontend_config.virtual_festival_discovery_dates.end",
                                ""
                              )}
                              label={"End date"}
                              hideTime
                              useMoment={true}
                              unclearable={true}
                              onChange={(e, v) =>
                                this.onChangeValue(
                                  "frontend_config.virtual_festival_discovery_dates.end",
                                  v.format("MM-DD-YYYY")
                                )
                              }
                              style={{ display: "inline-block" }}
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <h3 style={{ marginTop: 20 }}>Viewing duration restrictions</h3>
              <div>
                When customers unlock a virtual screening, they will have the
                "unlock window" duration to begin watching. Once they start
                watching (i.e., click "Play"), they will have the "watch window"
                duration to play the content.
              </div>
              <div>
                <DurationField
                  value={get(
                    this.state,
                    "values.virtual_festival_config.unlock_interval",
                    ""
                  )}
                  floatingLabelText="Unlock window"
                  floatingLabelFixed={true}
                  onChange={e =>
                    this.onChangeValue(
                      "virtual_festival_config.unlock_interval",
                      e.target.value
                    )
                  }
                />
                <DurationField
                  style={{ marginLeft: "1em" }}
                  value={get(
                    this.state,
                    "values.virtual_festival_config.watch_interval",
                    ""
                  )}
                  floatingLabelText="Watch window"
                  floatingLabelFixed={true}
                  onChange={e =>
                    this.onChangeValue(
                      "virtual_festival_config.watch_interval",
                      e.target.value
                    )
                  }
                />
              </div>
              {UserStore.isGod() ? (
                <div style={{ marginTop: 5 }}>
                  <Checkbox
                    label="[GOD ONLY] Cut off watch window immediately at availability window end"
                    checked={get(
                      this.state,
                      "values.virtual_festival_config.cutoff_watch_at_availability_end",
                      false
                    )}
                    onCheck={(e, checked) => {
                      this.onChangeValue(
                        "virtual_festival_config.cutoff_watch_at_availability_end",
                        checked
                      );
                    }}
                  />
                </div>
              ) : null}
              <h3 style={{ marginTop: 20 }}>Catalog settings</h3>
              <div>
                Customize what appears on your channel's main catalog page.
              </div>
              <div>
                <InjectedSelect
                  style={{ maxWidthc: "50%" }}
                  value={get(
                    this.state,
                    "values.virtual_festival_config.tag_section_order",
                    []
                  )}
                  types={["visibleTags"]}
                  multi={true}
                  label="Display section order"
                  placeholderText="Default: All sections shown"
                  labelFormatter={b => b.name}
                  eventBucketId={this.state.bucket.id}
                  onChange={selected => {
                    this.onChangeValue(
                      "virtual_festival_config.tag_section_order",
                      selected
                    );
                  }}
                />

                <Checkbox
                  style={{ marginTop: "1em" }}
                  label="Alphabetize screenings within each display section"
                  checked={get(
                    this.state,
                    "values.virtual_festival_config.tag_section_alphabetize",
                    false
                  )}
                  onCheck={(e, checked) => {
                    this.onChangeValue(
                      "virtual_festival_config.tag_section_alphabetize",
                      checked
                    );
                  }}
                />

                <Checkbox
                  label="Prioritize position of certain screenings within display sections"
                  checked={get(
                    this.state,
                    "values.virtual_festival_config.tag_section_use_priority_sort",
                    false
                  )}
                  onCheck={(e, checked) => {
                    this.onChangeValue(
                      "virtual_festival_config.tag_section_use_priority_sort",
                      checked
                    );
                  }}
                />

                {get(
                  this.state,
                  "values.virtual_festival_config.tag_section_use_priority_sort"
                ) ? (
                  <div style={{ marginLeft: "3em" }}>
                    <div
                      style={{
                        fontStyle: "italic",
                        marginBottom: "-0.25em",
                        color: "rgb(33, 33, 33)"
                      }}
                    >
                      Screenings added below will be sorted to the beginning of
                      every display section they are visible within.
                    </div>
                    <InjectedSelect
                      style={{ maxWidth: "100%" }}
                      value={
                        this.state.values.virtual_festival_config
                          .tag_section_priority_sort_events
                      }
                      types={["events"]}
                      multi={true}
                      label="Priority screening order"
                      placeholderText="No priority screenings added, will use default order"
                      labelFormatter={e => e.name}
                      filterBy={e => !!e.is_virtual}
                      eventBucketId={this.state.bucket.id}
                      onChange={selected => {
                        this.onChangeValue(
                          "virtual_festival_config.tag_section_priority_sort_events",
                          selected
                        );
                      }}
                    />
                  </div>
                ) : null}

                {/*
                <Label>Screening order within each display section</Label>
                {(() => {
                  const value =
                    this.state.values &&
                    this.state.values.virtual_festival_config
                      ? this.state.values.virtual_festival_config
                          .tag_section_alphabetize
                        ? "tag_section_alphabetize"
                        : this.state.values.virtual_festival_config
                            .tag_section_use_manual_sort
                        ? "tag_section_use_manual_sort"
                        : "default"
                      : null;

                  return (
                    <div>
                      <RadioButtonGroup
                        name="mode"
                        style={{ marginLeft: "0em" }}
                        valueSelected={value}
                        onChange={(e, v) => {
                          const values = this.state.values;
                          values.virtual_festival_config.tag_section_alphabetize =
                            v === "tag_section_alphabetize";
                          values.virtual_festival_config.tag_section_use_manual_sort =
                            v === "tag_section_use_manual_sort";
                          this.setState({
                            dirty: true,
                            values
                          });
                        }}
                      >
                        <RadioButton
                          value="default"
                          label={"Default order by creation time"}
                        />
                        <RadioButton
                          value="tag_section_alphabetize"
                          label={"Alphabetical order by screening name"}
                        />
                        <RadioButton
                          value="tag_section_use_manual_sort"
                          label={
                            <span>
                              Manual order
                              {value === "tag_section_use_manual_sort" ? (
                                <span style={{ fontStyle: "italic" }}>
                                  {" "}
                                  (add screenings below to force their position)
                                </span>
                              ) : (
                                ""
                              )}
                            </span>
                          }
                        />
                      </RadioButtonGroup>
                    </div>
                  );
                })()}
                */}

                <div style={{ marginTop: "1em" }}>
                  <strong>Did you know?</strong> You can customize the display
                  mode of each section (poster view, cover view, and more)
                  independently via the "Tags" section in the lefthand sidebar.
                </div>
              </div>
              <h3 style={{ marginTop: 20 }}>System notification emails</h3>
              <div>
                Configure automated system emails sent about virtual screenings
                in this event bucket.
              </div>
              <div style={{ marginTop: "0.5em" }}>
                <Checkbox
                  label="Send notification email 10 minutes before pre-ordered content becomes available"
                  checked={true}
                  disabled
                />
                {/* <Checkbox
                  label="Send notification email 24 hours before pre-ordered content becomes available"
                  checked={get(
                    this.state,
                    "values.virtual_festival_config.send_preorder_soon_reminders",
                    false
                  )}
                  onCheck={(e, checked) => {
                    this.onChangeValue(
                      "virtual_festival_config.send_preorder_soon_reminders",
                      checked
                    );
                  }}
                /> */}
                <Checkbox
                  label={
                    "Send warning email 24 hours before any still-unwatched ticket is going to expire"
                  }
                  checked={get(
                    this.state,
                    "values.virtual_festival_config.send_expiring_reminders",
                    false
                  )}
                  onCheck={(e, checked) => {
                    this.onChangeValue(
                      "virtual_festival_config.send_expiring_reminders",
                      checked
                    );
                  }}
                />
              </div>
              <h3 style={{ marginTop: 20 }}>
                Default pre-roll and post-roll content
              </h3>
              <div>
                Configure your default pre-roll and post-roll content. You can
                override these on a per-screening basis.
              </div>
              <div style={{ marginTop: 10 }}>
                <Label>Pre-roll virtual content</Label>
                <div
                  style={{
                    width: "100%",
                    overflowX: "auto",
                    margin: "-10px 0"
                  }}
                >
                  <VirtualContentEditor
                    style={{ width: "min-content", margin: "0 -10px" }}
                    useBeta={get(
                      this.state,
                      "values.virtual_festival_config.use_beta_vod_flow"
                    )}
                    value={get(this.state, "values.virtual_content_preroll")}
                    onChange={v =>
                      this.onChangeValue("virtual_content_preroll", v)
                    }
                  />
                </div>
              </div>
              <div style={{ marginTop: 15 }}>
                <Label>Post-roll virtual content</Label>
                <div
                  style={{
                    width: "100%",
                    overflowX: "auto",
                    margin: "-10px 0"
                  }}
                >
                  <VirtualContentEditor
                    style={{ width: "min-content", margin: "0 -10px" }}
                    useBeta={get(
                      this.state,
                      "values.virtual_festival_config.use_beta_vod_flow"
                    )}
                    value={get(this.state, "values.virtual_content_postroll")}
                    onChange={v =>
                      this.onChangeValue("virtual_content_postroll", v)
                    }
                  />
                </div>
              </div>
            </div>
            <div>
              <h3 style={{ marginTop: 20 }}>
                Hybrid integration
                <InlineNoticeLabel
                  label="Beta"
                  style={{
                    verticalAlign: 2,
                    marginLeft: 6,
                    fontWeight: "bold"
                  }}
                />
              </h3>
              <div style={{ marginBottom: 5 }}>
                Settings for integrated in-person and virtual screenings.
              </div>
              <div>
                <Checkbox
                  label="Display virtual screenings in native schedule alongside physical events"
                  checked={this.state.values.schedule_include_virtual || false}
                  onCheck={this.onChange.bind(this, "schedule_include_virtual")}
                />
              </div>
            </div>
            <div style={{ marginTop: 30 }}>
              {saveButton}
              <RaisedButton
                label="Close"
                style={{ marginLeft: 7 }}
                onTouchTap={() => this.setState({ showVirtualSettings: false })}
              />
            </div>
          </div>
        </BaseOverlay>
        <BaseOverlay
          open={this.state.showAdditionalSettingsModal}
          onRequestClose={() =>
            this.setState({ showAdditionalSettingsModal: false })
          }
          hideTitleAndFooterBorders={true}
          showCloseButton={true}
        >
          <div>
            <h2>Additional settings</h2>
            <div style={{ marginTop: 20 }}>
              <h3>Pass settings</h3>
              <div>
                <Checkbox
                  label="By default, require passholders to reserve tickets (overridable on a per-pass-bucket or per-event basis)"
                  checked={
                    this.state.values.passholder_ticket_required_default ||
                    false
                  }
                  onCheck={this.onChange.bind(
                    this,
                    "passholder_ticket_required_default"
                  )}
                />
              </div>
              <div>
                <Checkbox
                  label="Allow passholders to order tickets online"
                  checked={
                    this.state.values.passholder_web_orders_allowed || false
                  }
                  onCheck={this.onChange.bind(
                    this,
                    "passholder_web_orders_allowed"
                  )}
                />
              </div>
              <div>
                <Checkbox
                  label="Email ticket order confirmations to passholders"
                  checked={
                    this.state.values.passholder_order_email_confirmation ||
                    false
                  }
                  onCheck={this.onChange.bind(
                    this,
                    "passholder_order_email_confirmation"
                  )}
                />
              </div>
              {/* <div>
                <Checkbox
                  label="Enable one-step passholder ticket orders (no confirmation screen)"
                  checked={this.state.values.passholder_one_click || false}
                  onCheck={this.onChange.bind(this, "passholder_one_click")}
                />
              </div> */}
              <div>
                <Checkbox
                  label="Allow pass transfers"
                  checked={
                    this.state.values.passholder_transfer_allowed || false
                  }
                  onCheck={this.onChange.bind(
                    this,
                    "passholder_transfer_allowed"
                  )}
                />
              </div>
            </div>
            {this.state.values.mode !== "pass_only" ? (
              <div style={{ marginTop: 20 }}>
                <h3>Lock standalone tickets</h3>
                <div>
                  By default, when standalone tickets are enabled, anyone can
                  order them.
                  <br />
                  If you "lock" standalone tickets, users have to enter a
                  password before they're allowed to buy tickets.
                </div>
                <div style={{ marginTop: 10 }}>
                  <Checkbox
                    label="Standalone ticket sales locked"
                    checked={
                      this.state.values.standalone_ticket_sales_locked || false
                    }
                    onCheck={this.onChange.bind(
                      this,
                      "standalone_ticket_sales_locked"
                    )}
                  />
                </div>
                {this.state.values.standalone_ticket_sales_locked ? (
                  <div>
                    <TextField
                      value={get(
                        this.state,
                        "values.standalone_ticket_sales_locked_password",
                        ""
                      )}
                      floatingLabelText="Standalone ticket sales password"
                      floatingLabelFixed={true}
                      hintText="•••••••••"
                      onChange={e =>
                        this.onChangeValue(
                          "standalone_ticket_sales_locked_password",
                          e.target.value
                        )
                      }
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
            <div style={{ marginTop: 20 }}>
              <h3>Rush line settings</h3>
              <div>
                Settings related to selling tickets at the door using Rush Line
                mode on the{" "}
                <a
                  href="https://help.eventive.org/eventive-scanner"
                  target="_blank"
                >
                  Eventive Scanner
                </a>
                .
              </div>
              <div style={{ marginTop: 10 }}>
                <Checkbox
                  label="Enable Rush Line ticket sales & pass scanning"
                  checked={this.state.values.rush_line_enabled || false}
                  onCheck={this.onChange.bind(this, "rush_line_enabled")}
                />
              </div>
              <div>
                <Checkbox
                  label="Set a default rush line ticket price"
                  checked={
                    this.state.values.rush_line_default_price ||
                    this.state.showRushLinePriceEditor
                  }
                  onCheck={(e, checked) => {
                    this.setState({ showRushLinePriceEditor: checked }, () => {
                      !checked &&
                        this.onChangeValue("rush_line_default_price", null);
                    });
                  }}
                />
                {this.state.showRushLinePriceEditor ||
                  this.state.values.rush_line_default_price ? (
                  <CurrencyField
                    floatingLabelText="Default rush line ticket price"
                    floatingLabelFixed={true}
                    placeholder="$0.00"
                    value={this.state.values.rush_line_default_price}
                    onChange={e =>
                      this.setState({ showRushLinePriceEditor: true }, () =>
                        this.onChangeValue(
                          "rush_line_default_price",
                          e.target.value
                        )
                      )
                    }
                  />
                ) : null}
              </div>
            </div>
            <div style={{ marginTop: 20 }}>
              <h3>Box office settings</h3>
              <div>
                Settings related to selling tickets, passes, & items using the{" "}
                <Link to={`/event_buckets/${this.state.bucket.id}/box_office`}>
                  Eventive Box Office
                </Link>
                .
              </div>
              <div style={{ marginTop: 10 }}>
                <Checkbox
                  label="Auto-open PDF tickets/pass labels for printing after placing an order"
                  checked={this.state.values.box_office_auto_open}
                  onCheck={(e, checked) => {
                    this.onChangeValue("box_office_auto_open", checked);
                  }}
                />
                <Checkbox
                  label="Auto-populate the previous order's event when selling single tickets to a single event"
                  checked={this.state.values.box_office_auto_populate}
                  onCheck={(e, checked) => {
                    this.onChangeValue("box_office_auto_populate", checked);
                  }}
                />
                <Checkbox
                  label={`Generate ticket-sized (2.5" x 6") tickets for printing, instead of letter-sized`}
                  checked={this.state.values.box_office_ticket_printer}
                  onCheck={(e, checked) => {
                    this.onChangeValue("box_office_ticket_printer", checked);
                  }}
                />
                {this.state.values.box_office_ticket_printer ? (
                  <Checkbox
                    label={`Use Boca format for box office tickets`}
                    checked={this.state.values.box_office_use_boca}
                    onCheck={(e, checked) => {
                      this.onChangeValue("box_office_use_boca", checked);
                    }}
                  />
                ) : null}
                <Checkbox
                  label={`Show per-transaction option to mark box office tickets as scanned immediately`}
                  checked={this.state.values.box_office_show_mark_scanned}
                  onCheck={(e, checked) => {
                    this.onChangeValue("box_office_show_mark_scanned", checked);
                  }}
                />
                <Checkbox
                  label={`Enable box office shift tracking (cash in/out)`}
                  checked={this.state.values.box_office_shift_tracking}
                  onCheck={(e, checked) => {
                    this.onChangeValue("box_office_shift_tracking", checked);
                  }}
                />
                {(() => {
                  const addFee =
                    this.state.values.box_office_service_fee ||
                    this.state.showBoxOfficeServiceFee;

                  return (
                    <div>
                      <Checkbox
                        label={`Add box office per-item service fee`}
                        checked={addFee}
                        onCheck={() => {
                          this.setState(
                            { showBoxOfficeServiceFee: !addFee },
                            () => {
                              if (!this.state.showBoxOfficeServiceFee)
                                this.onChangeValue("box_office_service_fee", 0);
                            }
                          );
                        }}
                      />
                      {addFee ? (
                        <CurrencyField
                          floatingLabelText="Box office service fee"
                          floatingLabelFixed={true}
                          placeholder="$0.00"
                          style={{ marginTop: "-0.5em", marginLeft: "2.5em" }}
                          value={this.state.values.box_office_service_fee}
                          onChange={e =>
                            this.onChangeValue(
                              "box_office_service_fee",
                              e.target.value
                            )
                          }
                        />
                      ) : null}
                    </div>
                  );
                })()}
              </div>
            </div>
            <div style={{ marginTop: 20 }}>
              <h3>Custom native site domain</h3>
              <Checkbox
                label="Custom native site domain enabled"
                style={{ marginBottom: 10 }}
                checked={this.state.showCustomDomainField}
                onCheck={this.onCustomDomainCheck}
              />
              {this.state.showCustomDomainField ? (
                <div>
                  <div>
                    A custom domain must be a subdomain on another domain which
                    you control; for example, "events.yourorganization.com".
                    After entering the domain here, please contact your Eventive
                    account represenative. We will then provide a CNAME record
                    that you will need to add to your domain's DNS
                    configuration.
                  </div>
                  <TextField
                    value={get(this.state, "values.custom_domain", "")}
                    floatingLabelText="Custom domain"
                    floatingLabelFixed={true}
                    onChange={e =>
                      this.onChangeValue("custom_domain", e.target.value)
                    }
                  />
                </div>
              ) : null}
            </div>
            <div style={{ marginTop: 20 }}>
              <h3>Advanced ticketing settings</h3>
              <Checkbox
                label={`Allow passholder at-the-door instant ticket oversell`}
                checked={
                  this.state.values.passholder_instant_ticket_oversell_allowed
                }
                onCheck={(e, checked) => {
                  this.onChangeValue(
                    "passholder_instant_ticket_oversell_allowed",
                    checked
                  );
                }}
              />
              <Checkbox
                label={
                  <span>
                    Close online ticket sales prior to event start time{" "}
                    <i>(not recommended)</i>
                  </span>
                }
                checked={this.state.showCutoffEditor}
                onCheck={(e, checked) => {
                  this.setState(
                    { showCutoffEditor: !this.state.showCutoffEditor },
                    () =>
                      this.onChangeValue(
                        "ticket_sales_cutoff",
                        this.state.showCutoffEditor
                          ? 30 * 60 * 1000 /* 30 min */
                          : 0
                      )
                  );
                }}
              />
              {this.state.showCutoffEditor ? (
                <div style={{ marginLeft: 40 }}>
                  <div>
                    Online sales will close{" "}
                    <strong>
                      {juration.stringify(
                        this.state.values.ticket_sales_cutoff / 1000
                      )}
                    </strong>{" "}
                    (editable below) before each event start time and show as
                    "Box office only". You can customize the error message shown
                    to customers by editing the "Box office only error message"
                    in the Configurator.
                  </div>
                  <DurationField
                    value={get(this.state, "values.ticket_sales_cutoff", "")}
                    floatingLabelText="Ticket sales cutoff"
                    floatingLabelFixed={true}
                    onChange={e =>
                      this.onChangeValue("ticket_sales_cutoff", e.target.value)
                    }
                  />
                </div>
              ) : null}
              <Checkbox
                label={
                  <span>
                    Disable standalone ticket transfer for all users{" "}
                    <i>(not recommended)</i>
                  </span>
                }
                style={{ marginBottom: 10 }}
                checked={
                  this.state.values.ticket_transfer_for_all_users_disabled
                }
                onCheck={(e, checked) => {
                  this.onChangeValue(
                    "ticket_transfer_for_all_users_disabled",
                    checked
                  );
                }}
              />
            </div>
            {this.state.values.credits_enabled ? (
              <div style={{ marginTop: 20 }}>
                <h3>Credits</h3>
                <CurrencyField
                  floatingLabelText="Face credit price (undiscounted)"
                  floatingLabelFixed={true}
                  placeholder="$0.00"
                  style={{ marginTop: "-0.75em" }}
                  value={this.state.values.credits_face_price}
                  onChange={e =>
                    this.onChangeValue("credits_face_price", e.target.value)
                  }
                />

                <Label>Levels</Label>
                <div style={{ margin: "-1.5em -3em 0" }}>
                  <ul style={{ listStyle: "none" }}>
                    {this.state.values.credits_levels.map((v, i) => {
                      return (
                        <li style={{ marginBottom: "-0.5em", fontSize: 16 }}>
                          Quantity:&nbsp;
                          <TextField
                            name={`credits_levels_quantity-${i}`}
                            placeholder=""
                            value={v.quantity}
                            style={{ marginLeft: 5, width: 50 }}
                            onChange={e => {
                              this.state.values.credits_levels[i].quantity =
                                e.target.value;
                              this.onChangeValue(
                                "credits_levels",
                                this.state.values.credits_levels
                              );
                            }}
                          />
                          for
                          <CurrencyField
                            name={`credits_levels_price-${i}`}
                            placeholder="$0.00"
                            value={v.price}
                            onChange={e => {
                              this.state.values.credits_levels[i].price =
                                e.target.value;
                              this.onChangeValue(
                                "credits_levels",
                                this.state.values.credits_levels
                              );
                            }}
                            style={{
                              marginLeft: 7,
                              width: 150,
                              maxWidth: "25%"
                            }}
                          />
                          <FlatButton
                            style={{ marginLeft: 7, minWidth: 30 }}
                            label="×"
                            onTouchTap={() => {
                              this.state.values.credits_levels.splice(i, 1);
                              this.onChangeValue(
                                "credits_levels",
                                this.state.values.credits_levels
                              );
                            }}
                          />
                        </li>
                      );
                    })}
                    <li style={{ marginTop: "1em" }}>
                      <RaisedButton
                        label="Add another level"
                        onTouchTap={() =>
                          this.onChangeValue(
                            "credits_levels",
                            this.state.values.credits_levels.concat({})
                          )
                        }
                      />
                    </li>
                  </ul>
                </div>

                <div>
                  <InjectedSelect
                    style={{ maxWidth: "50%" }}
                    value={this.state.values.credits_allowed_pass_buckets}
                    types={["pass_buckets"]}
                    multi={true}
                    label="Specific pass buckets allowed to purchase credits"
                    placeholderText="Default: All customers allowed to purchase credits"
                    labelFormatter={b => b.name}
                    eventBucketId={this.state.bucket.id}
                    onChange={selected => {
                      this.onChangeValue(
                        "credits_allowed_pass_buckets",
                        selected
                      );
                    }}
                  />
                </div>

                <div>
                  <TextField
                    floatingLabelText="Credits discount percentage (0-1)"
                    floatingLabelFixed={true}
                    value={this.state.values.credits_discount.percent_off}
                    onChange={e => {
                      this.state.values.credits_discount.percent_off =
                        e.target.value;
                      this.onChangeValue(
                        "credits_discount",
                        this.state.values.credits_discount
                      );
                    }}
                  />

                  <InjectedSelect
                    style={{ maxWidth: "50%" }}
                    value={this.state.values.credits_discount.pass_buckets}
                    types={["pass_buckets"]}
                    multi={true}
                    label="Credits discount applicable pass buckets"
                    labelFormatter={b => b.name}
                    eventBucketId={this.state.bucket.id}
                    onChange={selected => {
                      this.state.values.credits_discount.pass_buckets =
                        selected;
                      this.onChangeValue(
                        "credits_discount",
                        this.state.values.credits_discount
                      );
                    }}
                  />
                </div>
              </div>
            ) : null}
            {UserStore.isGod() ? (
              <div style={{ marginTop: 20 }}>
                <h3>
                  Advanced options <InlineNoticeLabel label="God only" />
                </h3>
                <div>
                  <SelectField
                    value={this.state.values.class}
                    onChange={(e, i, value) =>
                      this.onChangeValue("class", value)
                    }
                    style={{ marginTop: "-0.5em", marginBottom: "0.5em" }}
                  >
                    <MenuItem value={"festival"} primaryText={"Festival"} />
                    <MenuItem value={"yearround"} primaryText={"Year-round"} />
                  </SelectField>
                </div>
                <Checkbox
                  label="Enable order-all-starred option via schedule"
                  checked={this.state.values.enable_order_all_starred}
                  onCheck={(e, checked) => {
                    this.onChangeValue("enable_order_all_starred", checked);
                  }}
                />
                <Checkbox
                  label="Enable guest checkout"
                  checked={this.state.values.enable_guest_checkout}
                  onCheck={(e, checked) => {
                    this.onChangeValue("enable_guest_checkout", checked);
                  }}
                />
                <Checkbox
                  label="Enable cart"
                  checked={this.state.values.enable_cart}
                  onCheck={(e, checked) => {
                    this.onChangeValue("enable_cart", checked);
                  }}
                />
                <Checkbox
                  label="Enable ticket credits"
                  checked={this.state.values.credits_enabled}
                  onCheck={(e, checked) => {
                    this.onChangeValue("credits_enabled", checked);
                  }}
                />
                <Checkbox
                  label="Enable balloting"
                  checked={this.state.values.enable_balloting}
                  onCheck={(e, checked) => {
                    this.onChangeValue("enable_balloting", checked);
                  }}
                />
                <Checkbox
                  label="Enable custom event IDs"
                  checked={this.state.values.enable_custom_event_ids}
                  onCheck={(e, checked) => {
                    this.onChangeValue("enable_custom_event_ids", checked);
                  }}
                />
                <Checkbox
                  label="Enable Eventival login"
                  checked={this.state.values.enable_eventival_login}
                  onCheck={(e, checked) => {
                    this.onChangeValue("enable_eventival_login", checked);
                  }}
                />
                <Checkbox
                  label="Enable NFC badges at check-in"
                  checked={this.state.values.enable_nfc}
                  onCheck={(e, checked) => {
                    this.onChangeValue("enable_nfc", checked);
                  }}
                />
                <Checkbox
                  label="Enable Print Traffic / Title Delivery mode"
                  style={{ marginTop: 10 }}
                  checked={this.state.values.enable_title_delivery}
                  onCheck={(e, checked) => {
                    this.onChangeValue("enable_title_delivery", checked);
                  }}
                />
                {this.state.values.enable_title_delivery ? (
                  <div style={{ marginLeft: "2.5rem", marginTop: "-0.5rem" }}>
                    <Label>Print delivery mode</Label>
                    {(() => {
                      const value =
                        this.state.values &&
                        this.state.values.title_delivery_config &&
                        this.state.values.title_delivery_config
                          .accepted_print_formats &&
                        this.state.values.title_delivery_config
                          .accepted_print_formats[0];

                      return (
                        <div>
                          <RadioButtonGroup
                            name="mode"
                            style={{ marginLeft: "0em" }}
                            valueSelected={value}
                            onChange={(e, v) => {
                              const values = this.state.values;
                              values.title_delivery_config.accepted_print_formats =
                                [v];
                              this.setState({
                                dirty: true,
                                values
                              });
                            }}
                          >
                            <RadioButton value="dcp" label={"DCP"} />
                            <RadioButton
                              value="digital_file"
                              label={"Digital file (non-DCP)"}
                            />
                          </RadioButtonGroup>
                        </div>
                      );
                    })()}
                  </div>
                ) : null}

                <Checkbox
                  label="Enable Eventive Virtual"
                  style={{ marginTop: 10, marginBottom: 0 }}
                  checked={this.state.values.enable_virtual_festival}
                  onCheck={(e, checked) => {
                    this.onChangeValue("enable_virtual_festival", checked);
                  }}
                />
                <Checkbox
                  label="[REQUIRES CUSTOM AGREEMENT] Defer all unlock fees"
                  checked={this.state.values.defer_all_unlock_fees}
                  onCheck={(e, checked) => {
                    this.onChangeValue("defer_all_unlock_fees", checked);
                  }}
                />
                <Checkbox
                  label="Eventive Virtual enable watermarking"
                  checked={get(
                    this.state.values,
                    "virtual_festival_config.watermark_enabled",
                    false
                  )}
                  onCheck={(e, checked) => {
                    this.onChangeValue(
                      "virtual_festival_config.watermark_enabled",
                      checked
                    );
                  }}
                />
                {get(
                  this.state.values,
                  "virtual_festival_config.watermark_enabled",
                  false
                ) ? (
                  <div>
                    <TextField
                      style={{ marginLeft: "2.5em", marginTop: "-0.5em" }}
                      floatingLabelText="Watermark title"
                      floatingLabelFixed={true}
                      value={get(
                        this.state.values,
                        "virtual_festival_config.watermark_title"
                      )}
                      onChange={e => {
                        this.onChangeValue(
                          "virtual_festival_config.watermark_title",
                          e.target.value
                        );
                      }}
                    />
                    <SelectField
                      value={get(
                        this.state.values,
                        "virtual_festival_config.watermark_location"
                      )}
                      onChange={(e, i, value) =>
                        this.onChangeValue(
                          "virtual_festival_config.watermark_location",
                          value
                        )
                      }
                      style={{
                        marginLeft: "1em",
                        verticalAlign: -2,
                        width: 160
                      }}
                    >
                      <MenuItem
                        value={"bottom-right"}
                        primaryText={"Bottom right"}
                      />
                      <MenuItem value={"top-left"} primaryText={"Top left"} />
                    </SelectField>
                  </div>
                ) : null}
              </div>
            ) : null}
            <div style={{ marginTop: 20 }}>
              {saveButton}
              <RaisedButton
                label="Close"
                style={{ marginLeft: 7 }}
                onTouchTap={() =>
                  this.setState({ showAdditionalSettingsModal: false })
                }
              />
            </div>
          </div>
        </BaseOverlay>
      </div>
    );
  }
}

export default connectToStores(EventBucketSettings);



// WEBPACK FOOTER //
// ./src/app/components/event-bucket-settings.jsx